import classNames from 'classnames';
import { CellAction, useCellActionsExecutor } from 'context/CellActions';
import _ from 'lodash';
import { TextBrandProps } from 'modules/BrandDefinition';
import {
  useBrandColorsForRelation,
  useBrandFontsForRelation,
  useBrandStylesForRelation,
} from 'modules/BrandDefinition/hooks';
import { confirmLexicalStateInTextComponent } from 'modules/draftjs2lexical';
import { LexicalTextEditor } from 'modules/Lexical/components/LexicalTextEditor';
import { useLexicalTextEditor } from 'modules/Lexical/hooks/useLexicalTextEditor';
import { useLexicalUndo } from 'modules/Lexical/hooks/useLexicalUndo';
import { useReferencesMap } from 'modules/References/hooks/useReferencesMap';
import React, { MutableRefObject, useRef, useMemo } from 'react';
import EditorToolbar from 'components/EditorToolbar';
import UndoPortal from 'components/UndoRedo/UndoPortal';
import { useHistoryGlobalState } from 'hooks/useHistoryGlobalState';
import { areThereHorizontalNeighbors } from 'utils/rowsHeight/areThereHorizontalNeighbors';
import { TextEditorHook } from './hooks/TextEditorHook';
import useBrandStyle from './hooks/useBrandStyle';
import useCell from './hooks/useCell';
import useEditMode from './hooks/useEditMode';
import { useHistoryExtraState } from './hooks/useHistoryExtraState';
import { usePaddingEffect } from './hooks/usePaddingEffect';
import useResizeObserver from './hooks/useResizeObserver';
import useSave from './hooks/useSave';
import useStyles from './hooks/useStyles';
import { TextProps } from './models';
import css from './styles.module.scss';
import { getContentMinHeight } from './utils/getContentMinHeight';
import { stylesToCSS } from './utils/styles';

export function getTextDOMId(relationId: string): string {
  return `text-cell-${relationId}`;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const Text: React.FunctionComponent<TextProps> = (props) => { // NOSONAR
  const {
    layoutId,
    projectType,
    relation,
    sectionStyles,
    images,
    placeholderMinHeight,
    shrinkable,
    editMode,
    notEditable,
    layoutRelations,
    ssi,
    document: originalDocument,
    activeLayer,
    toggleAutoFitContent,
    isAutoFitContent,
  } = props;

  const relationId = relation?.get('id');

  const colors = useBrandColorsForRelation(relationId);
  const fonts = useBrandFontsForRelation(relationId);
  const brandStyles = useBrandStylesForRelation(relationId);

  const relationStyles = relation?.get('styles')?.get(activeLayer);
  const document = useMemo(
    () => originalDocument && confirmLexicalStateInTextComponent(
      originalDocument,
      relationStyles?.toJS() ?? null,
      fonts,
    ),
    // fonts are intentionally skipped from the deps
    [originalDocument, relationStyles], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const stylesHook = useStyles({ ...props, colors, fonts, brandStyles });

  const lexicalState = document?.get('lexicalState') ?? null;
  const lexicalHook = useLexicalTextEditor(stylesHook.stylesSetters.brandStyleChanged);
  const editorHook: TextEditorHook = lexicalHook;

  const wrapperRef = useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
  const cellHook = useCell(
    props,
    wrapperRef,
    editorHook,
    stylesHook.styles,
  );

  const brandStyleHook = useBrandStyle(
    { ...props, colors, fonts, brandStyles },
    editorHook,
    stylesHook,
  );

  useResizeObserver(wrapperRef.current, () => cellHook.resetHeight());
  usePaddingEffect(relation, layoutRelations, stylesHook, cellHook, isAutoFitContent);

  const saveHandler = useSave(
    props,
    editorHook,
    stylesHook,
    cellHook,
  );
  useEditMode(
    { ...props, colors, brandStyles },
    editorHook,
    stylesHook,
    cellHook,
    brandStyleHook.setBrandStyle,
    saveHandler,
  );

  const history = useHistoryExtraState(props, stylesHook, cellHook);
  const undoHook = useLexicalUndo(lexicalHook.ref);
  useHistoryGlobalState(editMode, undoHook.isUndoDisabled);

  useCellActionsExecutor(CellAction.SET_CURSOR_ON_ABBREVIATION, editorHook.setCursorOnAbbreviation);
  // lexical abbreviation works without APPLY_SELECTION action executor:
  // useCellActionsExecutor(CellAction.APPLY_SELECTION, ...);

  const referencesMap = useReferencesMap(
    document,
    relation,
    activeLayer,
  );

  const textBrandProps = useMemo(
    (): TextBrandProps => ({ brandStyle: stylesHook.styles.brandStyle, colors, fonts }),
    [colors, fonts, stylesHook.styles.brandStyle],
  );

  const isMultiColumn = areThereHorizontalNeighbors(relation.get('id'), layoutRelations);
  const isAutoHeight = editorHook.hasCustomToken && !isMultiColumn;
  const currentClientHeight = wrapperRef?.current?.clientHeight || props.minCellHeight;
  const contentMinHeight = getContentMinHeight(stylesHook.styles, currentClientHeight);

  return (
    <div
      role='presentation'
      className={classNames(css.Text, {
        [css.severalColumns]: cellHook.props.cellsCount > 1,
        [css.noMinHeight]: editorHook.hasTextContent || editMode || shrinkable || cellHook.props.cellHeight,
        [css.fullHeight]: cellHook.props.isLastCell || cellHook.props.cellHeight,
      })}
      style={stylesToCSS(stylesHook.styles, { images, placeholderMinHeight })}
    >
      {editMode && (
        <>
          <UndoPortal
            undo={undoHook.undo}
            redo={undoHook.redo}
            isRedoDisabled={undoHook.isRedoDisabled}
            isUndoDisabled={undoHook.isUndoDisabled}
          />
          <EditorToolbar
            layoutId={layoutId}
            projectType={projectType}
            returnFocusToEditor={editorHook.returnFocusToEditor}
            editorProps={editorHook.props}
            editorSetters={editorHook.setters}
            styles={stylesHook.styles}
            stylesSetters={stylesHook.stylesSetters}
            cellProps={cellHook.props}
            toggleCellHeight={cellHook.toggleCellHeight}
            toggleColumnWidth={cellHook.toggleCellWidth}
            isAutoFitContent={props.isAutoFitContent}
            toggleAutoFitContent={toggleAutoFitContent}
            isAutoHeight={isAutoHeight}
            beforeAutoFitMouseDown={_.noop}
            setBrandStyle={brandStyleHook.setBrandStyle}
            sectionStyles={sectionStyles}
            contentMinHeight={contentMinHeight}
          />
        </>
      )}
      <div>
        <LexicalTextEditor
          id={getTextDOMId(relation.get('id'))}
          wrapperRef={wrapperRef}
          ref={lexicalHook.ref}
          editorState={lexicalState}
          editMode={editMode}
          brandStyles={brandStyles}
          brandStyleChanged={stylesHook.styles.brandStyleChanged}
          brandProps={textBrandProps}
          onChange={lexicalHook.onChange}
          historyExtraState={history.extraState}
          historyExtraStateSetter={history.extraStateSetter}
          historyOnChange={undoHook.onChange}
          referencesMap={referencesMap}
          ssiPosition={notEditable && ssi?.get('position')}
          textDocument={document}
          relation={relation}
          saveHandler={saveHandler}
        />
      </div>
    </div>
  );
};

export default Text;
